import React, { useState } from 'react';
import { XMarkIcon, EyeIcon, EyeSlashIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Admin } from '../services/api';
import ConfirmModal from './ConfirmModal';

interface AdminEditModalProps {
  admin: Admin;
  onSave: (admin: Admin) => void;
  onClose: () => void;
  isLoading?: boolean;
  error?: string | null;
}

const AdminEditModal: React.FC<AdminEditModalProps> = ({ admin, onSave, onClose, isLoading = false, error: propError = null }) => {
  const [formData, setFormData] = useState({
    name: admin.name,
    email: admin.email,
    role: admin.role,
    isActive: admin.isActive,
    password: '', // Optional password field
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null); // Clear previous errors
    if (validateForm()) {
      // Check if there are any changes
      const hasChanges = 
        formData.name !== admin.name ||
        formData.email !== admin.email ||
        formData.role !== admin.role ||
        formData.isActive !== admin.isActive ||
        (formData.password && formData.password.trim() !== '');
      
      if (!hasChanges) {
        // No changes, just close the modal
        onClose();
        return;
      }
      
      // Show confirmation dialog
      setShowConfirmModal(true);
    }
  };

  const confirmSave = () => {
    const updateData = {
      ...admin,
      ...formData,
    };

    // Only include password if it was changed
    if (!formData.password) {
      const { password, ...dataWithoutPassword } = updateData;
      onSave(dataWithoutPassword);
    } else {
      onSave(updateData);
    }
    setShowConfirmModal(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Edit Admin</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isLoading}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Error Display */}
          {(error || propError) && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800 text-left">Error</h3>
                  <div className="mt-2 text-sm text-red-700">{error || propError}</div>
                </div>
              </div>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 text-left">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-imola-primary focus:border-imola-primary ${errors.name ? 'border-red-300' : ''
                  }`}
                required
                disabled={isLoading}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600 text-left">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 text-left">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-imola-primary focus:border-imola-primary ${errors.email ? 'border-red-300' : ''
                  }`}
                required
                disabled={isLoading}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 text-left">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 text-left">Role</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-imola-primary focus:border-imola-primary"
                disabled={isLoading}
              >
                <option value="STAFF">Staff</option>
                <option value="ADMIN">Admin</option>
                <option value="SUPER_ADMIN">Super Admin</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 text-left">New Password (Optional)</label>
              <div className="mt-1 relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`block w-full border border-gray-300 rounded-md px-3 py-2 pr-12 focus:outline-none focus:ring-1 focus:ring-imola-primary focus:border-imola-primary ${errors.password ? 'border-red-300' : ''
                    }`}
                  placeholder="Leave blank to keep current password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 z-20"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 text-left">{errors.password}</p>
              )}
              <p className="mt-1 text-xs text-gray-500 text-left">Leave blank to keep current password</p>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="h-4 w-4 text-imola-primary focus:ring-imola-primary border-gray-300 rounded"
                disabled={isLoading}
              />
              <label className="ml-2 block text-sm text-gray-900">
                Active Account
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 text-left">Created</label>
              <p className="mt-1 text-sm text-gray-500 text-left">
                {new Date(admin.createdAt).toLocaleDateString()} at {new Date(admin.createdAt).toLocaleTimeString()}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 text-left">Last Login</label>
              <p className="mt-1 text-sm text-gray-500 text-left">
                {admin.lastLogin
                  ? `${new Date(admin.lastLogin).toLocaleDateString()} at ${new Date(admin.lastLogin).toLocaleTimeString()}`
                  : 'Never'
                }
              </p>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-imola-primary"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || !formData.name.trim() || !formData.email.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-imola-primary border border-transparent rounded-md hover:bg-imola-primary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-imola-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        visible={showConfirmModal}
        title="Confirm Update"
        message={`Are you sure you want to update admin "${admin.name}"?`}
        confirmText="Update"
        cancelText="Cancel"
        onConfirm={confirmSave}
        onCancel={() => setShowConfirmModal(false)}
      />
    </div>
  );
};

export default AdminEditModal; 